import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database.types'

// Lazy initialization to avoid errors during SSR/testing
let supabaseInstance: SupabaseClient<Database> | null = null

/**
 * Creates and returns the Supabase client
 * Uses lazy initialization for better SSR compatibility
 */
function getSupabaseClient(): SupabaseClient<Database> {
  if (supabaseInstance) {
    return supabaseInstance
  }

  // Use shortened env var names to avoid Vite 7's env placeholder system
  // Vite 7 redacts vars containing "SUPABASE" or "KEY" as potential secrets
  const supabaseUrl = import.meta.env.VITE_SB_URL
  const supabaseAnonKey = import.meta.env.VITE_SB_ANONPUBLIC

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. Please check your .env file.'
    )
  }

  supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    global: {
      headers: {
        'x-application-name': 'eduhires',
      },
    },
  })

  return supabaseInstance
}

/**
 * The Supabase client instance
 * Access database and auth through this client
 */
export const supabase = getSupabaseClient()

/**
 * Type-safe wrapper for common Supabase errors
 */
export interface SupabaseError {
  message: string
  code?: string
  details?: string
  hint?: string
}

/**
 * Formats Supabase errors for user display
 */
export function formatSupabaseError(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred'
  }

  if (typeof error === 'string') {
    return error
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }

  return 'An unexpected error occurred'
}

/**
 * Common auth error codes and their user-friendly messages
 */
export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'invalid_credentials': 'Invalid email or password',
  'email_not_confirmed': 'Please verify your email address',
  'user_already_exists': 'If this email is not already registered, you will receive a confirmation email shortly',
  'weak_password': 'Password is too weak. Use at least 8 characters',
  'invalid_email': 'Please enter a valid email address',
  'rate_limit_exceeded': 'Too many attempts. Please try again later',
  'session_expired': 'Your session has expired. Please sign in again',
}

/**
 * Gets a user-friendly error message for auth errors
 */
export function getAuthErrorMessage(errorCode: string | undefined): string {
  if (!errorCode) {
    return 'An authentication error occurred'
  }
  return AUTH_ERROR_MESSAGES[errorCode] ?? 'An authentication error occurred'
}

/**
 * Formats auth errors from Supabase into user-friendly messages
 */
export function formatAuthError(error: { message?: string; code?: string | undefined; status?: number | undefined }): string {
  // Check for specific error codes first
  if (error.code && AUTH_ERROR_MESSAGES[error.code]) {
    return AUTH_ERROR_MESSAGES[error.code]
  }

  // Handle common Supabase auth error messages
  const message = error.message?.toLowerCase() ?? ''

  if (message.includes('invalid login credentials') || message.includes('invalid credentials')) {
    return 'Invalid email or password'
  }
  if (message.includes('email not confirmed')) {
    return 'Please verify your email address before signing in'
  }
  if (message.includes('user already registered') || message.includes('already exists')) {
    return 'If this email is not already registered, you will receive a confirmation email shortly'
  }
  if (message.includes('password')) {
    return 'Password must be at least 8 characters with letters and numbers'
  }
  if (message.includes('rate limit') || error.status === 429) {
    return 'Too many attempts. Please try again later'
  }
  if (message.includes('network') || message.includes('fetch')) {
    return 'Network error. Please check your connection'
  }

  // Return the original message or a generic error
  return error.message ?? 'An authentication error occurred'
}

// Re-export useful types
export type { Database } from '@/types/database.types'
export type {
  User,
  Session,
  AuthError,
  AuthChangeEvent,
} from '@supabase/supabase-js'
